import { Avatar } from '@amelio/ui-library';

import type { AssessmentEmployee } from '@/types/manager-assessment/Employee';

import { useAmelioTranslations } from '@/hooks';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

interface EmployeeSearchResultRowProps {
    employee: AssessmentEmployee;
    jobTitle: string;
    isSelected: boolean;
    /** The flow needs a competency profile and this employee has none, so the row cannot be picked. */
    isMissingProfile: boolean;
    onSelect: (employee: AssessmentEmployee) => void;
    onAssignProfile: (employeeId: string) => void;
}

/**
 * One option of the employee search. An employee the flow cannot assess without a competency
 * profile states the reason alongside the shortcut to fix it, rather than looking selectable and
 * failing on click.
 */
export const EmployeeSearchResultRow = ({
    employee,
    jobTitle,
    isSelected,
    isMissingProfile,
    onSelect,
    onAssignProfile,
}: EmployeeSearchResultRowProps) => {
    const { t } = useAmelioTranslations();

    const identity = (
        <>
            <Avatar
                src={getProfilePictureUrl(employee.avatarUrl) || undefined}
                alt={getFullName(employee)}
                initials={getInitials(employee)}
                bgColor={'purple'}
                size={'2xs'}
            />

            <div className={'flex flex-col gap-0.5 text-left'}>
                <span className={'text-[14px] font-medium text-gray-900'}>
                    {getFullName(employee)}
                </span>
                <span className={'text-[12px] text-gray-500'}>{jobTitle}</span>
            </div>
        </>
    );

    if (isMissingProfile) {
        return (
            <div
                role={'option'}
                aria-selected={false}
                aria-disabled
                className={
                    'flex min-h-[65px] w-full items-center gap-3 border-b border-gray-200 px-4 py-3 last:border-b-0'
                }
                data-testid={`employee-item-${employee.id}`}
            >
                {identity}

                <div
                    className={
                        'ml-auto flex w-80 max-w-1/2 shrink-0 flex-col items-end gap-0.5 pl-3 text-right text-balance'
                    }
                    data-testid={`employee-no-profile-${employee.id}`}
                >
                    <span className={'text-[12px] text-gray-500'}>
                        {t('managerAssessmentSetup.employeeHasNoCompetencyProfile')}
                    </span>

                    <button
                        type={'button'}
                        className={
                            'text-primary focus-visible:ring-primary-800 rounded-sm text-[12px] font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                        }
                        onClick={() => onAssignProfile(employee.id)}
                        data-testid={`employee-assign-profile-${employee.id}`}
                    >
                        {t('managerAssessmentSetup.assignCompetencyProfile')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <button
            type={'button'}
            role={'option'}
            aria-selected={isSelected}
            className={
                'flex h-[65px] w-full items-center gap-3 border-b border-gray-200 px-4 py-3 transition-colors last:border-b-0 hover:bg-gray-50'
            }
            onClick={() => onSelect(employee)}
            data-testid={`employee-item-${employee.id}`}
        >
            {identity}
        </button>
    );
};
